import type { CdmoImportRowState } from '../../lib/cdmoImport/validateRow';
import type { CdmoImportOutcome } from '../../lib/cdmoImport/importRows';

export const ROW_STATE_LABELS: Record<CdmoImportRowState, string> = {
  ready: 'Ready',
  warning: 'Ready with warnings',
  error: 'Error',
  duplicate: 'Duplicate',
};

export const OUTCOME_LABELS: Record<CdmoImportOutcome, string> = {
  created: 'Created',
  skipped_duplicate: 'Skipped: duplicate',
  skipped_error: 'Skipped: errors',
  skipped_by_admin: 'Skipped: unticked',
  failed: 'Failed',
};

export { EVIDENCE_GROUP_LABELS } from '../../lib/cdmoImport/columns';

/** Saves a file through a temporary object URL. */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Saves text as a CSV file. */
export function downloadCsv(content: string, fileName: string): void {
  downloadBlob(new Blob([content], { type: 'text/csv;charset=utf-8' }), fileName);
}

/**
 * Builds the AI-ready template (an Excel workbook generated from the importer's own columns and
 * option lists) and saves it. Loaded only when asked for, so the upload page stays light.
 */
export async function downloadAiTemplate(): Promise<void> {
  const [{ buildCdmoImportAiTemplate, CDMO_IMPORT_AI_TEMPLATE_FILE_NAME }, { XLSX_MIME_TYPE }] = await Promise.all([
    import('../../lib/cdmoImport/aiTemplate'),
    import('../../lib/xlsx/writeXlsx'),
  ]);
  downloadBlob(new Blob([buildCdmoImportAiTemplate()], { type: XLSX_MIME_TYPE }), CDMO_IMPORT_AI_TEMPLATE_FILE_NAME);
}

/** A `File`'s text, via FileReader (supported everywhere the app runs, and in the test DOM). */
export function readFileAsText(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error ?? new Error('The file could not be read.'));
    reader.readAsText(file, 'utf-8');
  });
}
